import React from "react";
import { Check, Flame } from "lucide-react";
import { SITE } from "../lib/data";
import SectionNumber from "./SectionNumber";
import Embers from "./Embers";

const FEATURES = [
  "Fűthető kerti kiülő",
  "Dézsa és jakuzzi",
  "Állatok közelsége",
  "Tisza-parti séta",
  "Saját kemence",
  "Csendes vidéki éjszakák",
];

export default function Guesthouse() {
  return (
    <section
      id="vendeghaz"
      data-testid="guesthouse-section"
      className="relative bg-white py-24 sm:py-32 overflow-hidden"
    >
      <SectionNumber n="04" />

      {/* Atmospheric warm background blobs */}
      <div
        aria-hidden="true"
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-[0.07] blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #FFA64D 0%, #D90429 55%, transparent 75%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full opacity-[0.06] blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #D90429 0%, #FFA64D 50%, transparent 70%)",
        }}
      />

      {/* Subtle ember particles (low count for elegance, light variant for white bg) */}
      <Embers count={14} variant="light" />

      {/* Vertical glowing ember line - left side decoration */}
      <div
        aria-hidden="true"
        className="hidden lg:block absolute left-6 top-[20%] bottom-[20%] w-px"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(217,4,41,0.4) 30%, rgba(217,4,41,0.4) 70%, transparent 100%)",
        }}
      >
        <span
          className="absolute -left-[3px] top-1/3 w-[7px] h-[7px] rounded-full bg-pkred"
          style={{
            boxShadow:
              "0 0 14px rgba(217,4,41,0.85), 0 0 28px rgba(255,140,50,0.5)",
            animation: "flicker 2.2s ease-in-out infinite",
          }}
        />
        <span
          className="absolute -left-[2px] top-2/3 w-[5px] h-[5px] rounded-full bg-pkred/80"
          style={{
            boxShadow: "0 0 10px rgba(217,4,41,0.7)",
            animation: "flicker 2.8s ease-in-out infinite 0.6s",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 lg:order-2 reveal">
          <span className="eyebrow">Vendégház</span>
          <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
            Maradj tovább<br />
            <span className="italic text-pkred">a tűz mellett.</span>
          </h2>
          <p className="mt-8 text-pkcharcoal/75 text-base sm:text-lg leading-relaxed">
            Fűthető kerti kiülő, állatok közelsége, dézsa, jakuzzi és falusi
            szálláshely – mindaz, amire szükséged van a teljes
            kikapcsolódáshoz.
          </p>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FEATURES.map((f) => (
              <li
                key={f}
                className="flex items-center gap-3 text-pkcharcoal/80 text-sm group"
              >
                <span className="w-7 h-7 rounded-full bg-pkbeige flex items-center justify-center shrink-0 group-hover:bg-pkred transition-colors duration-500">
                  <Check
                    className="w-3.5 h-3.5 text-pkred group-hover:text-white transition-colors duration-500"
                    strokeWidth={2.5}
                  />
                </span>
                {f}
              </li>
            ))}
          </ul>

          <a
            href={SITE.phoneHref}
            data-testid="guesthouse-cta"
            className="btn-fire mt-12"
          >
            Vendégház foglalás
          </a>
        </div>

        <div className="lg:col-span-7 lg:order-1 reveal">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-8 aspect-[4/5] overflow-hidden relative">
              <img
                src="/images/87msw6xa.jpg"
                alt="Vendégház"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
              />
              {/* Warm bottom glow on main image */}
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to top, rgba(217,4,41,0.25), transparent)",
                  mixBlendMode: "multiply",
                }}
              />
            </div>
            <div className="col-span-4 flex flex-col gap-4">
              <div className="aspect-square overflow-hidden">
                <img
                  src="/images/ffzlam8f.jpg"
                  alt="Török lámpák"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                />
              </div>
              <div className="aspect-square overflow-hidden">
                <img
                  src="/images/xo022efk.jpg"
                  alt="Dézsa"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                />
              </div>
              {/* "2026 Új szezon" with ember glow */}
              <div
                className="relative aspect-square overflow-hidden bg-pkred flex items-center justify-center text-white p-4"
                style={{
                  boxShadow:
                    "0 0 0 1px rgba(217,4,41,0.4), 0 20px 40px -10px rgba(217,4,41,0.45)",
                }}
              >
                <Embers count={5} />
                <div
                  className="absolute inset-0 pointer-events-none opacity-40"
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 100%, rgba(255,180,80,0.5) 0%, transparent 65%)",
                  }}
                />
                <div className="relative text-center">
                  <Flame
                    className="w-4 h-4 mx-auto mb-2 drop-shadow-[0_0_10px_rgba(255,180,80,0.9)]"
                    fill="currentColor"
                    strokeWidth={1.2}
                  />
                  <div className="font-serif text-3xl leading-none">2026</div>
                  <div className="text-[0.6rem] tracking-[0.3em] uppercase mt-2 opacity-90">
                    Új szezon
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

